import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, Box, Skeleton, Tab, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroPageContainer,
	CerebroSecondaryButton,
	MatchingFromDealDrawer,
	MatchingSearchDrawer,
} from '../../shared/components';
import { Urls, useApi, useAuth } from '../../shared/context';
import {
	QueryKeys,
	useDataRoomTeasersQuery,
	useDealRequestAllocationQuery,
	useMatchingQuery,
} from '../../shared/hooks';
import { DataRoomTeaser, DataRoomTeaserFile, DataRoomTeaserPreview, DealStatus } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { RemoveTeaserDialog } from './RemoveTeaserDialog';
import { RemoveTeaserFileDialog } from './RemoveTeaserFileDialog';
import { ResetTeaserDialog } from './ResetTeaserDialog';
import { SendBulkTeasersDialog } from './SendBulkTeasersDialog';
import { SendTeaserDialog } from './SendTeaserDialog';
import { TeaserDetail } from './TeaserDetail';
import { TeaserEmailPreview } from './TeaserEmailPreview';
import { TeaserList } from './TeaserList';
import { TeaserResponseForm } from './TeaserResponseForm';
import { TeaserSummaryForm } from './TeaserSummaryForm';
import { TeaserTrancheForm } from './TeaserTrancheForm';
import { RestoreTeaserDialog } from './RestoreTeaserDialog';
import { ToggleReferralLanguageDialog } from './ToggleReferralLanguageDialog';
import { AddMatchedTeasersDialog } from './AddMatchedTeasersDialog';
import { TeaserPageActionButton } from './TeaserPageActionButton';
import { BulkDeleteTeasersDialog } from './BulkDeleteTeasersDialog';
import { IgnoreAtLaunchDialog } from './IgnoreAtLaunchDialog';
import { TeaserPageLenderActionButton } from './TeaserPageLenderActionButton';
import { getApiResponseData } from '../../shared/utils/axios';
import { SuggestedLendersDrawer } from './SuggestedLendersDrawer';
import { BulkIgnoreAtLaunchDialog } from './BulkIgnoreAtLaunchDialog';
import { BulkIncludeAtLaunchDialog } from './BulkIncludeAtLaunchDialog';

export const EmployeeTeasersPage: React.FC = () => {
	const theme = useTheme();
	const { guid } = useParams<'guid'>();
	const { currentUser } = useAuth();
	const api = useApi();
	const queryClient = useQueryClient();
	const snackbar = useSnackbar();
	const teasersQuery = useDataRoomTeasersQuery(guid);

	const navigate = useNavigate();

	const allocationQuery = useDealRequestAllocationQuery(teasersQuery.data?.deal.guid ?? '', {
		enabled: teasersQuery.isSuccess,
		retry: false,
	});

	const matchingQuery = useMatchingQuery(teasersQuery.data?.deal.guid ?? '', {
		enabled: teasersQuery.isSuccess,
		retry: false,
	});
	const hasLaunched = teasersQuery.data?.deal.hasLaunched ?? false;

	const [tab, setTab] = React.useState('unsent');
	const [teaserViewDetails, setTeaserViewDetails] = React.useState<DataRoomTeaser | null>(null);
	const [teaserBeingSent, setTeaserBeingSent] = React.useState<DataRoomTeaser | null>(null);
	const [teaserBeingResponded, setTeaserBeingResponded] = React.useState<DataRoomTeaser | null>(null);
	const [teaserBeingEdited, setTeaserBeingEdited] = React.useState<DataRoomTeaser | null>(null);
	const [teaserBeingReset, setTeaserBeingReset] = React.useState<DataRoomTeaser | null>(null);
	const [teaserBeingDeleted, setTeaserBeingDeleted] = React.useState<DataRoomTeaser | null>(null);
	const [emailTeaserPreview, setEmailTeaserPreview] = React.useState<DataRoomTeaserPreview | null>(null);
	const [fileBeingDeleted, setFileBeingDeleted] = React.useState<DataRoomTeaserFile | null>(null);
	const [editingSummary, setEditingSummary] = React.useState<boolean>(false);
	const [bulkTeasersDialog, setSendBulkTeaserDialog] = React.useState<boolean>(false);
	const [bulkDeleteDialog, setBulkDeleteDialog] = React.useState<boolean>(false);
	const [bulkIgnoreAtLaunchDialog, setBulkIgnoreAtLaunchDialog] = React.useState<boolean>(false);
	const [bulkIncludeAtLaunchDialog, setBulkIncludeAtLaunchDialog] = React.useState<boolean>(false);
	const [selectedTeasers, setTeasersSelected] = React.useState<string[]>([]);
	const [teasersReady, setTeasersReady] = React.useState<boolean>(false);
	const [teaserRestore, setTeaserRestore] = React.useState<DataRoomTeaser | null>(null);
	const [toggleReferralLanguage, setToggleReferralLanguage] = React.useState<DataRoomTeaser | null>(null);
	const [addMatchedLenders, setAddMatchedLenders] = React.useState<boolean>(false);
	const [addFromOtherDeals, setAddFromOtherDeals] = React.useState<boolean>(false);
	const [ignoreLaunch, setIgnoreLaunch] = React.useState<DataRoomTeaser | null>(null);
	const [matchingSearchDrawer, setMatchingSearchDrawer] = React.useState<boolean>(false);
	const [SuggestedLenderDrawer, setSuggestedLendersDrawer] = React.useState<boolean>(false);
	const [loadingPrompt, setLoadingPrompt] = React.useState(false);
	const [initialPrompt, setInitialPrompt] = React.useState<string>('');

	const showTeaserInfoMessage =
		tab == 'unsent' &&
		teasersQuery.isSuccess &&
		teasersQuery.data &&
		!hideTeaserMessage(teasersQuery.data.deal.dealStatus);

	React.useEffect(() => {
		setTeasersReady(isTeaserReady(teasersQuery.data?.deal.dealStatus));
	}, [teasersQuery.data?.deal.dealStatus]);

	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	const setSelectedTeasers = (teasers?: string[]) => {
		setTeasersSelected(teasers ?? []);
	};

	const clearDialogsAndDrawers = (): void => {
		setTeaserBeingSent(null);
		setTeaserBeingResponded(null);
		setTeaserBeingEdited(null);
		setTeaserBeingReset(null);
		setTeaserBeingDeleted(null);
		setTeaserViewDetails(null);
		setEmailTeaserPreview(null);
		setFileBeingDeleted(null);
		setEditingSummary(false);
		setSendBulkTeaserDialog(false);
		setBulkIgnoreAtLaunchDialog(false);
		setBulkIncludeAtLaunchDialog(false);
		setTeasersReady(isTeaserReady(teasersQuery.data?.deal.dealStatus));
		setTeaserRestore(null);
		setToggleReferralLanguage(null);
		setAddMatchedLenders(false);
		setBulkDeleteDialog(false);
		setIgnoreLaunch(null);
		setMatchingSearchDrawer(false);
		setSuggestedLendersDrawer(false);
		setAddFromOtherDeals(false);
	};

	const closeFileBeingDeleted = (): void => {
		setFileBeingDeleted(null);
	};

	const onAddTeaser = (): void => {
		navigate(`/${guid}/${Urls.Matching.Root}`);
	};

	const handleOpenSuggestedLenders = async () => {
		try {
			setLoadingPrompt(true);
			const { data, error } = await getApiResponseData<{ prompt: string }>(
				api.get(CerebroApiEndpoints.SuggestedLenders.Prompt(teasersQuery.data?.deal.guid ?? ''))
			);

			if (error) throw new Error(error.response?.data ?? 'Failed to load prompt');
			setInitialPrompt(data?.prompt ?? '');
		} catch (err) {
			snackbar.enqueueSnackbar((err as Error).message, { variant: 'error' });
		} finally {
			setLoadingPrompt(false);
			setSuggestedLendersDrawer(true);
		}
	};

	const sendTeasers = useMutation(
		async () => {
			const pendingTeasers =
				teasersQuery.data?.teasers.filter(teaser => teaser.decisionStatus == 'Unsent')?.map(t => t.guid) ?? [];
			const teasersToSend = !!selectedTeasers && selectedTeasers.length > 0 ? selectedTeasers : pendingTeasers;
			const { data, error } = await getApiResponseData<number>(
				api.post(CerebroApiEndpoints.Teasers.SendMany(guid), teasersToSend)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (teaserCount: number | null) => {
				queryClient.invalidateQueries(QueryKeys.Deal(teasersQuery.data?.deal.guid ?? ''));
				queryClient.invalidateQueries(QueryKeys.Teasers.Root(guid));
				snackbar.enqueueSnackbar(`${teaserCount} teasers successfully sent.`, { variant: 'success' });
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: () => {
				setSendBulkTeaserDialog(false);
				setSelectedTeasers([]);
				setTeasersSelected([] as string[]);
			},
		}
	);

	return (
		<CerebroPageContainer maxWidth={'100%'}>
			<CerebroBreadcrumb>Teasers</CerebroBreadcrumb>

			{teasersQuery.isSuccess && (
				<Box sx={{ marginBottom: 3 }}>
					<TeaserPageLenderActionButton
						onAddLender={() => onAddTeaser()}
						onGetMatchedLenders={() => setAddMatchedLenders(true)}
						onSearchLender={() => setMatchingSearchDrawer(true)}
						onAddFromOtherDeal={() => setAddFromOtherDeals(true)}
					/>

					<TeaserPageActionButton
						tab={tab}
						selectedTeasers={selectedTeasers}
						teasersReady={teasersReady}
						teaserPreview={teasersQuery.data.teaserPreview}
						hasLaunched={hasLaunched}
						onTeaserPreview={setEmailTeaserPreview}
						onEditSummary={setEditingSummary}
						onBlukIgnoreAtLaunch={setBulkIgnoreAtLaunchDialog}
						onBlukIncludeAtLaunch={setBulkIncludeAtLaunchDialog}
						onBulkSend={setSendBulkTeaserDialog}
						onBulkDelete={setBulkDeleteDialog}
					/>

					<CerebroSecondaryButton
						sx={{ float: 'right' }}
						onClick={() => handleOpenSuggestedLenders()}
						disabled={!teasersQuery.isSuccess}
					>
						Suggested Lenders
					</CerebroSecondaryButton>
				</Box>
			)}

			{showTeaserInfoMessage && (
				<Alert
					severity="info"
					sx={{
						marginBottom: 3,
						boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
						border: 'none',
						backgroundColor: 'white',
						fontWeight: 'bold',
					}}
				>
					Pending teasers will be sent when the deal is launched.
				</Alert>
			)}

			<TabContext value={tab}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<TabList onChange={handleTabChange}>
						<Tab label="Unsent" value="unsent" />
						<Tab label="Pending Response" value="sent" />
						<Tab label="Accepted" value="accepted" />
						<Tab label="Declined" value="declined" />
						<Tab label="Deleted" value="deleted" />
					</TabList>
				</Box>

				{teasersQuery.isLoading && (
					<>
						<Skeleton height={200} />
						<Skeleton height={200} />
						<Skeleton height={200} />
					</>
				)}

				{teasersQuery.isSuccess && (
					<>
						<TabPanel value="unsent">
							<TeaserList
								dataRoomGuid={guid}
								teasers={teasersQuery.data.teasers
									.filter(teaser => teaser.decisionStatus == 'Unsent' && !teaser.isDeleted)
									?.sort((a, b) => a.bankName.localeCompare(b.bankName))}
								usedTranches={teasersQuery.data.usedTranches}
								currentUser={currentUser}
								tab="unsent"
								hasLaunched={hasLaunched}
								respondTeaser={setTeaserBeingResponded}
								sendTeaser={setTeaserBeingSent}
								editTeaser={setTeaserBeingEdited}
								removeTeaser={setTeaserBeingDeleted}
								resetTeaser={setTeaserBeingReset}
								viewTeaserDetails={setTeaserViewDetails}
								setSelectedTeasers={setSelectedTeasers}
								onToggleReferralLanguage={setToggleReferralLanguage}
								selectedTeasers={selectedTeasers}
								teasersReady={teasersReady}
								restoreTeaser={setTeaserRestore}
								onIgnoreLaunch={setIgnoreLaunch}
							/>
						</TabPanel>
						<TabPanel value="sent">
							<TeaserList
								dataRoomGuid={guid}
								teasers={teasersQuery.data.teasers
									.filter(teaser => teaser.decisionStatus == 'Sent' && !teaser.isDeleted)
									?.sort((a, b) => a.bankName.localeCompare(b.bankName))}
								usedTranches={teasersQuery.data.usedTranches}
								currentUser={currentUser}
								tab="sent"
								hasLaunched={hasLaunched}
								respondTeaser={setTeaserBeingResponded}
								sendTeaser={setTeaserBeingSent}
								editTeaser={setTeaserBeingEdited}
								removeTeaser={setTeaserBeingDeleted}
								resetTeaser={setTeaserBeingReset}
								viewTeaserDetails={setTeaserViewDetails}
								setSelectedTeasers={setSelectedTeasers}
								onToggleReferralLanguage={setToggleReferralLanguage}
								selectedTeasers={selectedTeasers}
								teasersReady={teasersReady}
								restoreTeaser={setTeaserRestore}
								onIgnoreLaunch={setIgnoreLaunch}
							/>
						</TabPanel>
						<TabPanel value="accepted">
							<TeaserList
								dataRoomGuid={guid}
								teasers={teasersQuery.data.teasers
									.filter(teaser => teaser.decisionStatus == 'Accepted' && !teaser.isDeleted)
									?.sort((a, b) => a.bankName.localeCompare(b.bankName))}
								usedTranches={teasersQuery.data.usedTranches}
								currentUser={currentUser}
								tab="accepted"
								hasLaunched={hasLaunched}
								respondTeaser={setTeaserBeingResponded}
								sendTeaser={setTeaserBeingSent}
								editTeaser={setTeaserBeingEdited}
								removeTeaser={setTeaserBeingDeleted}
								resetTeaser={setTeaserBeingReset}
								viewTeaserDetails={setTeaserViewDetails}
								onToggleReferralLanguage={setToggleReferralLanguage}
								setSelectedTeasers={() => {}}
								teasersReady={teasersReady}
								restoreTeaser={setTeaserRestore}
								onIgnoreLaunch={setIgnoreLaunch}
							/>
						</TabPanel>
						<TabPanel value="declined">
							<TeaserList
								dataRoomGuid={guid}
								teasers={teasersQuery.data.teasers
									.filter(teaser => teaser.decisionStatus == 'Declined' && !teaser.isDeleted)
									?.sort((a, b) => a.bankName.localeCompare(b.bankName))}
								usedTranches={teasersQuery.data.usedTranches}
								currentUser={currentUser}
								tab="declined"
								hasLaunched={hasLaunched}
								respondTeaser={setTeaserBeingResponded}
								sendTeaser={setTeaserBeingSent}
								editTeaser={setTeaserBeingEdited}
								removeTeaser={setTeaserBeingDeleted}
								resetTeaser={setTeaserBeingReset}
								viewTeaserDetails={setTeaserViewDetails}
								onToggleReferralLanguage={setToggleReferralLanguage}
								setSelectedTeasers={() => {}}
								teasersReady={teasersReady}
								restoreTeaser={setTeaserRestore}
								onIgnoreLaunch={setIgnoreLaunch}
							/>
						</TabPanel>
						<TabPanel value="deleted">
							<TeaserList
								dataRoomGuid={guid}
								teasers={teasersQuery.data.teasers
									.filter(teaser => teaser.isDeleted)
									?.sort((a, b) => a.bankName.localeCompare(b.bankName))}
								usedTranches={teasersQuery.data.usedTranches}
								currentUser={currentUser}
								tab="declined"
								hasLaunched={hasLaunched}
								respondTeaser={setTeaserBeingResponded}
								sendTeaser={setTeaserBeingSent}
								editTeaser={setTeaserBeingEdited}
								removeTeaser={setTeaserBeingDeleted}
								resetTeaser={setTeaserBeingReset}
								viewTeaserDetails={setTeaserViewDetails}
								onToggleReferralLanguage={setToggleReferralLanguage}
								setSelectedTeasers={() => {}}
								teasersReady={teasersReady}
								restoreTeaser={setTeaserRestore}
								onIgnoreLaunch={setIgnoreLaunch}
							/>
						</TabPanel>
					</>
				)}
			</TabContext>

			<RemoveTeaserDialog dataRoomGuid={guid} teaser={teaserBeingDeleted} close={clearDialogsAndDrawers} />
			<ResetTeaserDialog dataRoomGuid={guid} teaser={teaserBeingReset} close={clearDialogsAndDrawers} />
			<SendTeaserDialog dataRoomGuid={guid} teaser={teaserBeingSent} close={clearDialogsAndDrawers} />
			<RestoreTeaserDialog dataRoomGuid={guid} teaser={teaserRestore} close={clearDialogsAndDrawers} />

			<ToggleReferralLanguageDialog
				dataRoomGuid={guid}
				teaser={toggleReferralLanguage}
				close={clearDialogsAndDrawers}
			/>
			<TeaserDetail teaser={teaserViewDetails} close={clearDialogsAndDrawers} />

			<TeaserResponseForm dataRoomGuid={guid} teaser={teaserBeingResponded} close={clearDialogsAndDrawers} />
			<TeaserTrancheForm dataRoomGuid={guid} teaser={teaserBeingEdited} close={clearDialogsAndDrawers} />
			<TeaserEmailPreview dataRoomGuid={guid} preview={emailTeaserPreview} close={clearDialogsAndDrawers} />
			<TeaserSummaryForm
				dataRoomGuid={guid}
				isOpen={editingSummary}
				dataRoom={teasersQuery.data?.dataRoom}
				close={clearDialogsAndDrawers}
				onDeleteFile={setFileBeingDeleted}
			/>
			<RemoveTeaserFileDialog dataRoomGuid={guid} file={fileBeingDeleted} close={closeFileBeingDeleted} />
			<SendBulkTeasersDialog
				teasers={selectedTeasers}
				pendingTeaserCount={
					teasersQuery.data?.teasers.filter(
						teaser =>
							(teaser.decisionStatus == 'Unsent' || teaser.decisionStatus == 'Sent') && !teaser.isDeleted
					)?.length ?? 0
				}
				isOpen={bulkTeasersDialog}
				close={clearDialogsAndDrawers}
				onSubmit={sendTeasers.mutate}
			/>
			<AddMatchedTeasersDialog dataRoomGuid={guid} open={addMatchedLenders} close={clearDialogsAndDrawers} />
			<BulkDeleteTeasersDialog
				open={bulkDeleteDialog}
				dataRoomGuid={guid}
				selectedTeasers={selectedTeasers}
				teasers={teasersQuery.data?.teasers
					.filter(
						teaser =>
							(teaser.decisionStatus == 'Unsent' || teaser.decisionStatus == 'Sent') && !teaser.isDeleted
					)
					?.sort((a, b) => a.bankName.localeCompare(b.bankName))}
				setTeasersSelected={setTeasersSelected}
				close={clearDialogsAndDrawers}
			/>
			<BulkIgnoreAtLaunchDialog
				open={bulkIgnoreAtLaunchDialog}
				dataRoomGuid={guid}
				selectedTeasers={selectedTeasers}
				teasers={teasersQuery.data?.teasers
					.filter(
						teaser =>
							(teaser.decisionStatus == 'Unsent' || teaser.decisionStatus == 'Sent') && !teaser.isDeleted
					)
					?.sort((a, b) => a.bankName.localeCompare(b.bankName))}
				setTeasersSelected={setTeasersSelected}
				close={clearDialogsAndDrawers}
			/>
			<BulkIncludeAtLaunchDialog
				open={bulkIncludeAtLaunchDialog}
				dataRoomGuid={guid}
				selectedTeasers={selectedTeasers}
				teasers={teasersQuery.data?.teasers
					.filter(
						teaser =>
							(teaser.decisionStatus == 'Unsent' || teaser.decisionStatus == 'Sent') && !teaser.isDeleted
					)
					?.sort((a, b) => a.bankName.localeCompare(b.bankName))}
				setTeasersSelected={setTeasersSelected}
				close={clearDialogsAndDrawers}
			/>
			<IgnoreAtLaunchDialog dataRoomGuid={guid} teaser={ignoreLaunch} close={clearDialogsAndDrawers} />
			<MatchingSearchDrawer
				open={matchingSearchDrawer}
				close={clearDialogsAndDrawers}
				dealGuid={allocationQuery.data?.dealGuid}
				allocation={allocationQuery.data}
				tranches={matchingQuery.data?.evaluations}
			/>
			<MatchingFromDealDrawer
				open={addFromOtherDeals}
				close={clearDialogsAndDrawers}
				dealGuid={allocationQuery.data?.dealGuid}
				allocation={allocationQuery.data}
				tranches={matchingQuery.data?.evaluations}
			/>
			<SuggestedLendersDrawer
				open={SuggestedLenderDrawer}
				close={clearDialogsAndDrawers}
				dealGuid={allocationQuery.data?.dealGuid}
				loading={loadingPrompt}
				initialPrompt={initialPrompt}
			/>
		</CerebroPageContainer>
	);
};

function isTeaserReady(status: DealStatus | undefined) {
	return status === 'TermSheetIssued' || status === 'Active' || status === 'ActiveClosing' || status === 'Paused';
}

function hideTeaserMessage(status: DealStatus | undefined) {
	return (
		status === 'TermSheetIssued' ||
		status === 'Active' ||
		status === 'ClosedDiscarded' ||
		status === 'ClosedFunded' ||
		status === 'ActiveClosing' ||
		status === 'Paused'
	);
}
